import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useWorkspaceProfile } from 'utils/useWorkspaceProfile';
import { MODULE_DISABLED_EVENT } from 'constants.js';

/**
 * Whether `module` is switched off for the workspace.
 * `loading` depends only on the profile (and its disabledModules) being loaded. A failed profile fetch fails open
 * (module treated as enabled); a later 403 MODULE_DISABLED from a gated API still flips the view.
 * `isAdmin` is read from the `isAdmin` query param / router state and is `false` when unknown.
 */
export const useModuleDisabled = (module) => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const token = searchParams.get('token') || location.state?.token;
    const isAdmin = searchParams.get('isAdmin') === 'true' || location.state?.isAdmin === true;
    const [reportedDisabled, setReportedDisabled] = useState(false);

    const { data, isLoading, dataUpdatedAt, errorUpdatedAt } = useWorkspaceProfile(token);

    useEffect(() => {
        const onDisabled = (event) => {
            const reported = event?.detail?.module;
            if (reported === module || !reported) setReportedDisabled(true);
        };
        window.addEventListener(MODULE_DISABLED_EVENT, onDisabled);
        return () => window.removeEventListener(MODULE_DISABLED_EVENT, onDisabled);
    }, [module]);

    const disabledModules = Array.isArray(data?.disabledModules) ? data.disabledModules : [];
    return {
        // Only until the profile first settles; later refetches (reconnect, remount) must never bring the loader back.
        loading: !!token && isLoading && dataUpdatedAt === 0 && errorUpdatedAt === 0,
        disabled: disabledModules.includes(module) || reportedDisabled,
        disabledModules,
        isAdmin,
        companyName: data?.workspaceName,
    };
};
